import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../../components/Header";
import { useDarkMode } from "../../App";
import { useCurrentUser, useCurrentUserProfile } from "../../utils";
import VerifyEmailPlaceholder from "../../components/VerifyEmailPlaceholder";
import { VinDecodedData } from "../../services/vinDecoder";
import { LocationData } from "../../components/LocationInput";
import VinEntryStep from "./steps/VinEntryStep";
import VehicleDetailsStep from "./steps/VehicleDetailsStep";
import PhotosStep from "./steps/PhotosStep";
import PricingStep from "./steps/PricingStep";
import ReviewStep from "./steps/ReviewStep";
import DevWizardToolbar from "./DevWizardToolbar";
import { buildDevWizardFill, isDevWizardHelpersEnabled } from "./listingWizardDevFill";
import "./CreateListingWizard.css";

export interface WizardData {
  // VIN data
  vin: string;
  vinData: VinDecodedData | null;

  // Vehicle details
  year: number | null;
  make: string;
  model: string;
  trim: string;
  bodyStyle: string;
  driveType: string;
  fuelType: string;
  transmission: string;
  engine: string;
  exteriorColor: string;
  interiorColor: string;
  /** Sticky OEM exterior color from NeoVIN enrichment (may differ from exteriorColor). */
  suggestedExteriorColor: string | null;
  /** Sticky OEM interior color from NeoVIN enrichment (may differ from interiorColor). */
  suggestedInteriorColor: string | null;
  mileage: string;
  titleStatus: string;

  // Photos & videos
  vehicleImages: File[];
  /** Validated pending listing videos (uploaded on publish). */
  vehicleVideos: Array<{
    file: File;
    durationSeconds: number;
    contentType: string;
  }>;
  titleImage: File | null;
  /** S3 key after title upload + verify (null until checked). */
  titleImageKey: string | null;
  /** Server OCR result: idle | checking | passed | failed | unavailable */
  titleVerifyStatus: "idle" | "checking" | "passed" | "failed" | "unavailable";
  titleVerifyMessage: string | null;

  // Pricing & Location
  price: string;
  description: string;
  location: LocationData | null;

  // Legacy Teoalida catalog fields (no longer written by the wizard; kept so
  // older in-flight sessions / types don't break).
  catalogSourceId: string | null;
  unmatchedCatalog: boolean;
  catalogMatchConfidence: number | null;
  catalogMatchStage: string | null;

  // Provider-agnostic VIN identity snapshot (MarketCheck via /vin/decode).
  identityProvider: string | null;
  identityProviderTier: string | null;
  identityStyleKey: string | null;
  identityVersion: string | null;
  identityConfidenceRaw: string | null;
  identityConfidenceScore: number | null;
  identityDisplayTitle: string | null;
  identitySnapshot: unknown | null;
  /** True once we've attempted (and possibly failed) NeoVIN enrichment. */
  enrichmentAttempted: boolean;
}

export const initialWizardData: WizardData = {
  vin: "",
  vinData: null,
  year: null,
  make: "",
  model: "",
  trim: "",
  bodyStyle: "",
  driveType: "",
  fuelType: "",
  transmission: "",
  engine: "",
  exteriorColor: "",
  interiorColor: "",
  suggestedExteriorColor: null,
  suggestedInteriorColor: null,
  mileage: "",
  titleStatus: "Clean",
  vehicleImages: [],
  vehicleVideos: [],
  titleImage: null,
  titleImageKey: null,
  titleVerifyStatus: "idle",
  titleVerifyMessage: null,
  price: "",
  description: "",
  location: null,
  catalogSourceId: null,
  unmatchedCatalog: false,
  catalogMatchConfidence: null,
  catalogMatchStage: null,
  identityProvider: null,
  identityProviderTier: null,
  identityStyleKey: null,
  identityVersion: null,
  identityConfidenceRaw: null,
  identityConfidenceScore: null,
  identityDisplayTitle: null,
  identitySnapshot: null,
  enrichmentAttempted: false,
};

const STEPS = [
  { id: 1, name: "VIN", label: "Enter VIN" },
  { id: 2, name: "Details", label: "Vehicle Details" },
  { id: 3, name: "Photos", label: "Photos & Videos" },
  { id: 4, name: "Pricing", label: "Pricing & Location" },
  { id: 5, name: "Review", label: "Review" },
];

const CreateListingWizard: React.FC = () => {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { emailVerified } = useCurrentUserProfile();
  const { darkMode } = useDarkMode();
  const [currentStep, setCurrentStep] = useState(1);
  const [wizardData, setWizardData] = useState<WizardData>(initialWizardData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const applyDevFill = useCallback((data: WizardData, step: number) => {
    setWizardData(data);
    setCurrentStep(Math.min(Math.max(step, 1), STEPS.length));
  }, []);

  // ?devFill=1&step=5 — bookmarkable one-liner for local UI testing.
  useEffect(() => {
    if (!isDevWizardHelpersEnabled()) return;
    if (searchParams.get("devFill") !== "1") return;

    const step = parseInt(searchParams.get("step") || "5", 10);
    let cancelled = false;

    void buildDevWizardFill().then((data) => {
      if (!cancelled) {
        applyDevFill(data, step);
        searchParams.delete("devFill");
        searchParams.delete("step");
        setSearchParams(searchParams, { replace: true });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, applyDevFill]);

  // Redirect if not logged in
  if (!user?.id) {
    navigate("/login");
    return null;
  }

  // Block the wizard entirely for unverified users. The server-side middleware
  // enforces this too, but rendering the form would only lead to a 403 on
  // submit — better to explain up front and offer the verification dialog.
  if (emailVerified === false) {
    return (
      <>
        <Header />
        <VerifyEmailPlaceholder
          title="Verify your email to list a car"
          description="We'll email you a verification link to confirm your address, then you can post your listing."
        />
      </>
    );
  }

  const updateWizardData = (updates: Partial<WizardData>) => {
    setWizardData((prev) => ({ ...prev, ...updates }));
  };

  const goToNextStep = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <VinEntryStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={goToNextStep}
            darkMode={darkMode}
          />
        );
      case 2:
        return (
          <VehicleDetailsStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            darkMode={darkMode}
          />
        );
      case 3:
        return (
          <PhotosStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            darkMode={darkMode}
          />
        );
      case 4:
        return (
          <PricingStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            darkMode={darkMode}
          />
        );
      case 5:
        return (
          <ReviewStep
            data={wizardData}
            onBack={goToPreviousStep}
            onEdit={goToStep}
            darkMode={darkMode}
            userId={user.id}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <div className={`wizard-container ${darkMode ? "dark" : ""}`} data-testid="create-listing-wizard">
        {/* Progress Bar */}
        <div className="wizard-progress" data-testid="wizard-step-indicator">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`wizard-step ${currentStep >= step.id ? "active" : ""} ${
                currentStep === step.id ? "current" : ""
              }`}
              data-testid={`wizard-step-${step.id}`}
              onClick={() => goToStep(step.id)}
            >
              <div className="step-number">{step.id}</div>
              <div className="step-label">{step.label}</div>
              {index < STEPS.length - 1 && <div className="step-connector" />}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="wizard-content">{renderStep()}</div>

        <DevWizardToolbar
          currentStep={currentStep}
          onFill={applyDevFill}
          onReset={() => {
            setWizardData(initialWizardData);
            setCurrentStep(1);
          }}
          darkMode={darkMode}
        />
      </div>
    </>
  );
};

export default CreateListingWizard;
